import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { HiChevronDown, HiChevronRight, HiDownload, HiPencil, HiRefresh, HiSparkles, HiX } from 'react-icons/hi';
import { FaProjectDiagram } from 'react-icons/fa';
import { PageLayout } from '../../components/layout';
import { SearchInput } from '../../components/Search';
import { Button } from '../../components/Button';
import { Table } from '../../components/Table';
import { TableColumn } from '../../components/Table/Table.types';
import Pagination from '../../components/Pagination';
import AppChart from '../../components/charts/AppChart';
import {
  getReferralPerformances,
  getReferralTreeByUser,
  getReferredListForUser,
  updateUserReferralCode,
} from '../../services/DashboardService';
import { ReferralPerformance, ReferralTreeNode, ReferralTreeResponse } from '../../types/dashboard.types';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { downloadCsv } from '../../utils/csvDownload';

type ReferralTableRow = ReferralPerformance & {
  rank: number;
};

type PaginatedReferralResponse = {
  data?: ReferralPerformance[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  summary?: {
    totalReferrals?: number;
    topPerformer?: ReferralPerformance | null;
  };
};

const PAGE_SIZE = 20;

const summaryCardClassName =
  'rounded-2xl border border-white/60 dark:border-white/10 bg-white/75 dark:bg-slate-900/55 backdrop-blur-sm px-5 py-4 shadow-sm';

const getValidationError = (referralCode: string): string | null => {
  const trimmed = referralCode.trim();

  if (!trimmed) {
    return 'Referral code is required.';
  }

  if (trimmed.length < 5) {
    return 'Referral code must be at least 5 characters long.';
  }

  if (/\s/.test(trimmed)) {
    return 'Referral code cannot contain spaces.';
  }

  if (!/^[A-Za-z0-9]+$/.test(trimmed)) {
    return 'Referral code can contain only letters and numbers.';
  }

  return null;
};

const getErrorMessage = (error: unknown): string => {
  const responseMessage = (error as any)?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }

  if ((error as Error)?.message) {
    return (error as Error).message;
  }

  return 'Failed to update referral code.';
};

// ─── List-view tree (HTML) ────────────────────────────────────────────────
const cloneTreeNode = (node: ReferralTreeNode): ReferralTreeNode => ({
  userId: node.userId,
  userName: node.userName,
  email: node.email,
  referralCode: node.referralCode,
  totalDirectReferrals: node.totalDirectReferrals,
  children: node.children.map(cloneTreeNode),
});

const buildGuiTreeFromResponse = (tree: ReferralTreeResponse): ReferralTreeNode => {
  let current = cloneTreeNode(tree.downline);
  [...tree.upline].reverse().forEach((ancestor) => {
    current = {
      userId: ancestor.userId,
      userName: ancestor.userName,
      email: ancestor.email,
      referralCode: ancestor.referralCode,
      totalDirectReferrals: 1,
      children: [current],
    };
  });
  return current;
};

const ReferralTreeBranch = ({
  node,
  selectedUserId,
  collapsedNodeIds,
  onToggle,
}: {
  node: ReferralTreeNode;
  selectedUserId: number | null;
  collapsedNodeIds: Set<number>;
  onToggle: (nodeId: number) => void;
}) => {
  const isSelected = selectedUserId === node.userId;
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsedNodeIds.has(node.userId);
  return (
    <div className="space-y-2">
      <div className={`rounded-xl border px-4 py-3 transition-colors ${
        isSelected
          ? 'border-indigo-400/70 bg-indigo-50/80 ring-1 ring-indigo-400/30 dark:border-indigo-500/60 dark:bg-indigo-900/25'
          : 'border-white/60 bg-white/70 dark:border-white/10 dark:bg-slate-900/55'
      }`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onToggle(node.userId)}
                aria-label={isCollapsed ? `Expand ${node.userName}` : `Collapse ${node.userName}`}
                className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-white/60 bg-white/70 text-gray-600 transition-colors hover:bg-white dark:border-white/10 dark:bg-slate-800/70 dark:text-gray-300 dark:hover:bg-slate-700"
              >
                {isCollapsed ? <HiChevronRight className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
              </button>
            ) : (
              <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center text-gray-300 dark:text-gray-600">•</span>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{node.userName}</p>
                {isSelected && (
                  <span className="shrink-0 rounded-md bg-indigo-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
                    Selected
                  </span>
                )}
              </div>
              <p className="truncate text-xs text-gray-600 dark:text-gray-400">{node.email || 'No email'}</p>
              <p className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Code: {node.referralCode || '—'}</p>
            </div>
          </div>
          <span className="shrink-0 rounded-lg bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
            {node.children.length} ref{node.children.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      {hasChildren && !isCollapsed && (
        <div className="space-y-2 border-l-2 border-dashed border-indigo-200/60 pl-4 dark:border-white/10">
          {node.children.map((child) => (
            <ReferralTreeBranch
              key={child.userId}
              node={child}
              selectedUserId={selectedUserId}
              collapsedNodeIds={collapsedNodeIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Chart-view tree (ECharts) ─────────────────────────────────────────────
const buildEChartsNode = (node: ReferralTreeNode, selectedUserId: number | null): any => {
  const isSelected = node.userId === selectedUserId;
  return {
    name: node.userName,
    value: `${node.email || '—'} · Code: ${node.referralCode || '—'}`,
    itemStyle: isSelected
      ? { color: '#6366f1', borderColor: '#4f46e5', borderWidth: 2, shadowColor: 'rgba(99,102,241,0.5)', shadowBlur: 12 }
      : { color: 'rgba(241,245,249,0.95)', borderColor: '#cbd5e1', borderWidth: 1.5 },
    label: {
      color: isSelected ? '#ffffff' : '#334155',
      fontWeight: isSelected ? '700' : '500',
    },
    children: node.children.map((c) => buildEChartsNode(c, selectedUserId)),
  };
};

const buildReferralEChartsOption = (tree: ReferralTreeResponse, selectedUserId: number | null): any => {
  let current = buildEChartsNode(tree.downline, selectedUserId);
  [...tree.upline].reverse().forEach((ancestor) => {
    const isSelected = ancestor.userId === selectedUserId;
    current = {
      name: ancestor.userName,
      value: `${ancestor.email || '—'} · Code: ${ancestor.referralCode || '—'}`,
      itemStyle: isSelected
        ? { color: '#6366f1', borderColor: '#4f46e5', borderWidth: 2, shadowColor: 'rgba(99,102,241,0.5)', shadowBlur: 12 }
        : { color: 'rgba(241,245,249,0.95)', borderColor: '#cbd5e1', borderWidth: 1.5 },
      label: {
        color: isSelected ? '#ffffff' : '#334155',
        fontWeight: isSelected ? '700' : '500',
      },
      children: [current],
    };
  });
  return {
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15,23,42,0.88)',
      borderColor: 'rgba(255,255,255,0.12)',
      textStyle: { color: '#f1f5f9', fontSize: 13 },
      formatter: (params: any) =>
        `<strong style="font-size:13px">${params.name}</strong><br/><span style="color:#94a3b8;font-size:11px">${params.value}</span>`,
    },
    series: [
      {
        type: 'tree',
        data: [current],
        top: '6%',
        bottom: '6%',
        left: '14%',
        right: '22%',
        layout: 'orthogonal',
        orient: 'LR',
        symbol: 'roundRect',
        symbolSize: [170, 52],
        edgeShape: 'curve',
        label: {
          position: 'inside',
          fontSize: 12,
          overflow: 'truncate',
          width: 158,
          lineHeight: 18,
          fontFamily: 'Inter, system-ui, sans-serif',
        },
        leaves: {
          label: { position: 'inside' },
        },
        lineStyle: {
          color: '#94a3b8',
          width: 1.5,
          curveness: 0.6,
        },
        initialTreeDepth: -1,
        expandAndCollapse: true,
        animationDuration: 500,
        animationDurationUpdate: 700,
        animationEasing: 'cubicOut',
      },
    ],
  };
};

export default function ReferralPerformancePage() {
  const [referrals, setReferrals] = useState<ReferralPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [resetPagination, setResetPagination] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalReferralsCount, setTotalReferralsCount] = useState(0);
  const [topPerformer, setTopPerformer] = useState<ReferralPerformance | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [downloadingUserId, setDownloadingUserId] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<ReferralPerformance | null>(null);
  const [editReferralCode, setEditReferralCode] = useState('');
  const [editError, setEditError] = useState('');
  const [savingReferralCode, setSavingReferralCode] = useState(false);
  const [selectedTreeUserId, setSelectedTreeUserId] = useState<number | null>(null);
  const [referralTree, setReferralTree] = useState<ReferralTreeResponse | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState('');
  const [isTreeModalOpen, setIsTreeModalOpen] = useState(false);
  const [treeViewMode, setTreeViewMode] = useState<'chart' | 'list'>('chart');
  const [collapsedTreeNodeIds, setCollapsedTreeNodeIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const timeout = setTimeout(() => {
      const normalized = searchValue.trim();
      setSearchTerm(normalized);
      setCurrentPage(1);
      setResetPagination((value) => !value);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchValue]);

  const loadReferralPerformances = useCallback(async (page: number, search?: string, from?: string, to?: string) => {
    setLoading(true);

    try {
      const response = await getReferralPerformances({
        page,
        pageSize: PAGE_SIZE,
        search,
        fromDate: from || undefined,
        toDate: to || undefined,
      });

      const payload = (response.data || {}) as PaginatedReferralResponse;
      const rows = Array.isArray(payload.data) ? payload.data : [];

      setReferrals(rows);
      setTotalRecords(typeof payload.total === 'number' ? payload.total : rows.length);
      setTotalReferralsCount(
        typeof payload.summary?.totalReferrals === 'number'
          ? payload.summary.totalReferrals
          : 0,
      );
      setTopPerformer(payload.summary?.topPerformer || null);
    } catch (error) {
      console.error('Error loading referral performances:', error);
      setReferrals([]);
      setTotalRecords(0);
      setTotalReferralsCount(0);
      setTopPerformer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferralPerformances(currentPage, searchTerm || undefined, fromDate || undefined, toDate || undefined);
  }, [currentPage, searchTerm, fromDate, toDate, loadReferralPerformances]);

  const rowsWithRank = useMemo<ReferralTableRow[]>(() => {
    const startRank = (currentPage - 1) * PAGE_SIZE;

    return referrals.map((row, index) => ({
      ...row,
      rank: startRank + index + 1,
    }));
  }, [currentPage, referrals]);

  const openEditReferralModal = (row: ReferralPerformance) => {
    setSelectedReferral(row);
    setEditReferralCode(row.referralCode || '');
    setEditError('');
    setIsEditModalOpen(true);
  };

  const closeEditReferralModal = () => {
    setIsEditModalOpen(false);
    setSelectedReferral(null);
    setEditReferralCode('');
    setEditError('');
  };

  const handleSaveReferralCode = async () => {
    if (!selectedReferral) {
      return;
    }

    const validationError = getValidationError(editReferralCode);

    if (validationError) {
      setEditError(validationError);
      return;
    }

    setSavingReferralCode(true);
    setEditError('');

    try {
      const response = await updateUserReferralCode(
        selectedReferral.userId,
        editReferralCode.trim(),
      );

      const updatedReferrersCount = Number(response?.data?.updatedReferrersCount || 0);
      showSuccessToast(`Referral code updated successfully. ${updatedReferrersCount} linked users were migrated.`);

      closeEditReferralModal();
      await loadReferralPerformances(currentPage, searchTerm || undefined);
    } catch (error) {
      const message = getErrorMessage(error);
      setEditError(message);
      showErrorToast(message);
    } finally {
      setSavingReferralCode(false);
    }
  };

  const handleViewReferralTree = async (row: ReferralPerformance) => {
    setIsTreeModalOpen(true);
    setSelectedTreeUserId(row.userId);
    setTreeLoading(true);
    setTreeError('');
    try {
      const response = await getReferralTreeByUser(row.userId, 6, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setReferralTree((response.data || null) as ReferralTreeResponse | null);
    } catch (error) {
      setReferralTree(null);
      setTreeError(getErrorMessage(error));
    } finally {
      setTreeLoading(false);
    }
  };

  const closeTreeModal = () => {
    setIsTreeModalOpen(false);
    setSelectedTreeUserId(null);
    setReferralTree(null);
    setTreeError('');
    setCollapsedTreeNodeIds(new Set());
  };

  const echartsOption = useMemo(() => {
    if (!referralTree) return null;
    return buildReferralEChartsOption(referralTree, selectedTreeUserId);
  }, [referralTree, selectedTreeUserId]);

  const guiTree = useMemo(() => {
    if (!referralTree) return null;
    return buildGuiTreeFromResponse(referralTree);
  }, [referralTree]);

  const toggleTreeNode = (nodeId: number) => {
    setCollapsedTreeNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) { next.delete(nodeId); } else { next.add(nodeId); }
      return next;
    });
  };

  const handleDownloadReferrals = async (row: ReferralPerformance) => {
    setDownloadingUserId(row.userId);
    try {
      const response = await getReferredListForUser(row.userId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      const payload = response.data as { data: { userId: number; userName: string; email: string; signupDate: string | null }[]; total: number };
      const rows = Array.isArray(payload?.data) ? payload.data : [];

      if (!rows.length) {
        showErrorToast('No referrals found for the selected period.');
        return;
      }

      const csvRows = rows.map((r) => ({
        Name: r.userName,
        Email: r.email,
        'Signup date': r.signupDate ?? '',
      }));

      const dateSuffix = fromDate && toDate ? `_${fromDate}_to_${toDate}` : '';
      const safeName = (row.userName || `user-${row.userId}`).replace(/[^a-z0-9]/gi, '-').toLowerCase();
      downloadCsv(`referrals-${safeName}${dateSuffix}`, csvRows);
    } catch (error) {
      showErrorToast(getErrorMessage(error));
    } finally {
      setDownloadingUserId(null);
    }
  };

  const columns: TableColumn<ReferralTableRow>[] = [
    {
      id: 'rank',
      label: 'Rank',
      width: '90px',
      align: 'center',
      sortable: false,
      format: (value) => (
        <span className="inline-flex min-w-[3rem] items-center justify-center rounded-xl bg-primary-10 px-3 py-1.5 font-semibold text-primary-100 dark:bg-primary-100/15 dark:text-primary-10">
          #{value}
        </span>
      ),
    },
    {
      id: 'userName',
      label: 'Referrer',
      sortable: false,
      priority: 'high',
      format: (_, row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-900 dark:text-gray-100">{row.userName}</p>
          <p className="truncate text-sm text-gray-600 dark:text-gray-400">{row.email}</p>
        </div>
      ),
    },
    {
      id: 'referralCode',
      label: 'Referral code',
      sortable: false,
      priority: 'medium',
      format: (value) => (
        <span className="inline-flex rounded-xl border border-primary-100/15 bg-primary-10/80 px-3 py-1.5 font-medium uppercase tracking-[0.16em] text-primary-100 dark:border-white/10 dark:bg-slate-800/80 dark:text-gray-100">
          {value || '—'}
        </span>
      ),
    },
    {
      id: 'totalReferrals',
      label: 'Total referrals',
      sortable: false,
      align: 'right',
      format: (value) => (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {value.toLocaleString()}
        </span>
      ),
    },
    {
      id: 'actions',
      label: '',
      sortable: false,
      align: 'center',
      width: '120px',
      priority: 'high',
      format: (_, row) => (
        <div className="flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => handleViewReferralTree(row)}
            aria-label={`View referral tree for ${row.userName}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-primary-100/15 bg-primary-10/70 text-primary-100 transition-colors hover:bg-primary-10 dark:border-white/10 dark:bg-slate-800/70 dark:text-gray-200 dark:hover:bg-slate-700/70"
          >
            <FaProjectDiagram className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleDownloadReferrals(row)}
            disabled={downloadingUserId === row.userId}
            aria-label={`Download referrals CSV for ${row.userName}`}
            title="Download referrals as CSV"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-emerald-200/60 bg-emerald-50/70 text-emerald-700 transition-colors hover:bg-emerald-100/80 disabled:opacity-50 dark:border-emerald-400/20 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/40"
          >
            <HiDownload className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => openEditReferralModal(row)}
            aria-label={`Edit referral code for ${row.userName}`}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-primary-100 transition-colors hover:bg-primary-10 dark:text-gray-200 dark:hover:bg-slate-700/70"
          >
            <HiPencil className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <PageLayout
      title="Referral performance"
      subtitle="Track the users driving the most successful referrals through shared referral codes."
      actions={
        <Button
          variant="secondary"
          onClick={() => loadReferralPerformances(currentPage, searchTerm || undefined, fromDate || undefined, toDate || undefined)}
          loading={loading}
          startIcon={<HiRefresh className="h-5 w-5" />}
        >
          Refresh data
        </Button>
      }
      filters={
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="w-full md:max-w-md">
              <SearchInput
                value={searchValue}
                placeholder="Search by referrer, email, or referral code"
                onChange={setSearchValue}
              />
            </div>
            <div className="flex items-center gap-2 rounded-2xl border border-fuchsia-200/60 bg-fuchsia-50/80 px-4 py-3 text-sm text-fuchsia-800 dark:border-fuchsia-400/20 dark:bg-fuchsia-900/15 dark:text-fuchsia-100">
              <HiSparkles className="h-5 w-5" />
              <span>{totalRecords.toLocaleString()} referral performers found</span>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                From date
              </label>
              <input
                type="date"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                  setResetPagination((v) => !v);
                }}
                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-colors focus:border-primary-100 dark:border-white/15 dark:bg-slate-800 dark:text-gray-100"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                To date
              </label>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                  setResetPagination((v) => !v);
                }}
                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition-colors focus:border-primary-100 dark:border-white/15 dark:bg-slate-800 dark:text-gray-100"
              />
            </div>
            {(fromDate || toDate) && (
              <button
                type="button"
                onClick={() => {
                  setFromDate('');
                  setToDate('');
                  setCurrentPage(1);
                  setResetPagination((v) => !v);
                }}
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/10 dark:bg-slate-800/70 dark:text-gray-300 dark:hover:bg-slate-700/70"
              >
                <HiX className="h-4 w-4" />
                Clear dates
              </button>
            )}
            {fromDate && toDate && (
              <p className="self-end text-xs text-gray-500 dark:text-gray-400">
                Showing referrals for signups between <strong className="text-gray-700 dark:text-gray-200">{fromDate}</strong> and <strong className="text-gray-700 dark:text-gray-200">{toDate}</strong>
              </p>
            )}
          </div>
        </div>
      }
      footer={
        totalRecords > PAGE_SIZE ? (
          <Pagination
            totalRecords={totalRecords}
            perPageCount={PAGE_SIZE}
            reset={resetPagination}
            setPerPageDataSet={(start) => {
              const nextPage = Math.max(1, Math.ceil(start / PAGE_SIZE));
              setCurrentPage(nextPage);
            }}
          />
        ) : undefined
      }
    >
      <div className="space-y-6 p-4 md:p-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className={summaryCardClassName}>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Active referrers</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">{totalRecords.toLocaleString()}</p>
          </div>
          <div className={summaryCardClassName}>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Total referrals</p>
            <p className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">{totalReferralsCount.toLocaleString()}</p>
          </div>
          <div className={summaryCardClassName}>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Top performer</p>
            <p className="mt-3 truncate text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">{topPerformer?.userName || '—'}</p>
            <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-400">
              {topPerformer
                ? `${topPerformer.email || 'No email'} • ${topPerformer.totalReferrals.toLocaleString()} referrals`
                : 'No data yet'}
            </p>
          </div>
        </div>

        <Table
          data={rowsWithRank}
          columns={columns}
          loading={loading}
          hoverable
          emptyMessage="No referral performances found."
        />
      </div>

      {isTreeModalOpen && createPortal(
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/55"
          onClick={(e) => { if (e.target === e.currentTarget) closeTreeModal(); }}
        >
          <div className="flex min-h-full items-start justify-center px-4 py-8">
            <div className="w-full max-w-5xl rounded-2xl border border-white/20 bg-white/95 p-5 shadow-2xl dark:border-white/10 dark:bg-slate-900/95">

              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Referral explorer</p>
                  <h3 className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">Referral tree</h3>
                  {fromDate && toDate && (
                    <p className="mt-1 inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">
                      Filtered: {fromDate} → {toDate}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {/* View toggle */}
                  <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-gray-100/70 p-1 dark:border-white/10 dark:bg-slate-800/70">
                    <button
                      type="button"
                      onClick={() => setTreeViewMode('chart')}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                        treeViewMode === 'chart'
                          ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-700 dark:text-indigo-300'
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      Chart
                    </button>
                    <button
                      type="button"
                      onClick={() => setTreeViewMode('list')}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                        treeViewMode === 'list'
                          ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-700 dark:text-indigo-300'
                          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      List
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={closeTreeModal}
                    aria-label="Close referral explorer"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800"
                  >
                    <HiX className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Stats row */}
              {referralTree && (
                <div className="mt-4 grid grid-cols-1 gap-2 text-center sm:grid-cols-3">
                  <div className="rounded-xl border border-white/60 bg-white/70 px-3 py-2 dark:border-white/10 dark:bg-slate-800/70">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Levels above</p>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{referralTree.stats.uplineDepth}</p>
                  </div>
                  <div className="rounded-xl border border-white/60 bg-white/70 px-3 py-2 dark:border-white/10 dark:bg-slate-800/70">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Levels below</p>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{referralTree.stats.downlineDepth}</p>
                  </div>
                  <div className="rounded-xl border border-white/60 bg-white/70 px-3 py-2 dark:border-white/10 dark:bg-slate-800/70">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Total below</p>
                    <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{referralTree.stats.totalDownlineMembers}</p>
                  </div>
                </div>
              )}

              {/* Content area */}
              <div className="mt-4">
                {treeLoading && (
                  <div className="rounded-xl border border-white/60 bg-white/70 px-4 py-10 text-center text-sm text-gray-600 dark:border-white/10 dark:bg-slate-800/70 dark:text-gray-300">
                    Loading referral tree…
                  </div>
                )}

                {treeError && !treeLoading && (
                  <div className="rounded-xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-400/25 dark:bg-red-900/20 dark:text-red-300">
                    {treeError}
                  </div>
                )}

                {/* Chart view */}
                {echartsOption && !treeLoading && !treeError && treeViewMode === 'chart' && (
                  <div className="overflow-hidden rounded-xl border border-white/60 bg-white/40 dark:border-white/10 dark:bg-slate-800/40">
                    <AppChart option={echartsOption} height={520} />
                  </div>
                )}

                {/* List view */}
                {guiTree && !treeLoading && !treeError && treeViewMode === 'list' && (
                  <div className="rounded-xl border border-white/60 bg-white/40 p-4 dark:border-white/10 dark:bg-slate-800/40">
                    <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                      Click arrow to expand · collapse nodes
                    </p>
                    <div className="max-h-[520px] overflow-y-auto pr-1">
                      <ReferralTreeBranch
                        node={guiTree}
                        selectedUserId={selectedTreeUserId}
                        collapsedNodeIds={collapsedTreeNodeIds}
                        onToggle={toggleTreeNode}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {isEditModalOpen && selectedReferral && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/20 bg-white p-6 shadow-xl dark:border-white/10 dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Edit referral code
            </h3>

            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {selectedReferral.userName} ({selectedReferral.email})
            </p>

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                New referral code
              </label>
              <input
                value={editReferralCode}
                onChange={(event) => {
                  setEditReferralCode(event.target.value);
                  if (editError) {
                    setEditError('');
                  }
                }}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm uppercase tracking-[0.08em] text-gray-900 outline-none transition-colors focus:border-primary-100 dark:border-white/15 dark:bg-slate-800 dark:text-gray-100"
                placeholder="Enter referral code"
                autoFocus
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use only letters and numbers, minimum 5 characters.
              </p>
              {editError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{editError}</p>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={closeEditReferralModal}
                disabled={savingReferralCode}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveReferralCode}
                loading={savingReferralCode}
              >
                Update code
              </Button>
            </div>
          </div>
        </div>
      , document.body)}
    </PageLayout>
  );
}
